import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useEditor } from '@craftjs/core';
import { Badge } from '@/components/ui/badge';
import { Car, Calendar, Gauge, Settings } from 'lucide-react';
import { SimpleVehicle } from './VehicleCarousel';
import { useCurrency } from '@/hooks/useCurrency';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import type { PrecioWebModo } from '@/lib/precio-web';

export interface VehicleCardOverlayProps {
  vehicle: SimpleVehicle;
  compact?: boolean;
  radius?: number; // px
  shadow?: string; // tailwind class
  showNewBadge?: boolean;
  newBadgeText?: string;
  /** Ticket #171: cómo se muestran los dos precios (ver `lib/precio-web`). */
  precioModo?: PrecioWebModo;
}

export const VehicleCard2: React.FC<VehicleCardOverlayProps> = ({
  vehicle,
  compact = false,
  radius = 24,
  shadow = 'shadow-[0_6px_24px_rgba(0,0,0,0.15)]',
  showNewBadge = true,
  newBadgeText = 'Recién publicado',
  precioModo = 'descuento',
}) => {
  const { isEnabled } = useEditor((s) => ({ isEnabled: s.options.enabled }));
  const { t } = useTranslation();
  // Textos fijos (catalog.*); si la clave no resuelve, el español de siempre.
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    return !val || val === key ? fallback : val;
  };

  const {
    id,
    brand,
    model,
    price,
    year,
    mileage,
    transmission,
    main_image,
    status,
    created_at,
    discount_percentage,
    fuel_type,
    label,
  } = vehicle;

  const isNew = () => {
    if (!created_at) return false;
    const d = new Date();
    d.setDate(d.getDate() - 5);
    return new Date(created_at) > d;
  };

  // Get status chip label and color
  // Custom states with show_in_web=true are treated as "Disponible" unless they are Vendido/Reservado
  // `status.name` es VALOR (se compara); solo cambia la etiqueta visible
  // (vehicles.status: 'Reservado'/'Vendido' en es y pt, como siempre).
  const getStatus = () => {
    if (status?.name === 'Reservado') {
      return { label: t('vehicles.status.reserved'), className: 'bg-amber-500 text-white' };
    }
    if (status?.name === 'Vendido') {
      return { label: t('vehicles.status.sold'), className: 'bg-rose-600 text-white' };
    }
    // All other visible states (including custom ones) show as "Disponible"
    return { label: t('site.vehicles.available'), className: 'bg-emerald-600 text-white' };
  };

  const statusChip = getStatus();

  const { formatPrice: formatCLP } = useCurrency();

  const effectivePrice =
    price && discount_percentage && discount_percentage > 0
      ? Math.round(price * (1 - discount_percentage / 100))
      : price;

  const Title = (
    <h3 className="text-white font-semibold text-lg leading-tight line-clamp-1">
      {brand?.name} {model?.name}
    </h3>
  );

  // Ticket #171: dos formas de pago, cada una con su rótulo y sin tachar.
  const Price = effectivePrice && price && discount_percentage && discount_percentage > 0 && precioModo === 'contado_financiamiento' ? (
    <div className="flex flex-col leading-tight" data-precio-modo="contado_financiamiento">
      <span className="text-white/80 text-[10px] uppercase tracking-wide">{tx('catalog.card.financingPrice', 'Precio financiamiento')}</span>
      <span className="text-white font-bold text-base">{formatCLP(effectivePrice)}</span>
      <span className="text-white/80 text-[10px] uppercase tracking-wide mt-0.5">{tx('catalog.card.cashPrice', 'Precio contado')}</span>
      <span className="text-white/90 font-semibold text-xs">{formatCLP(price)}</span>
    </div>
  ) : (
    <div className="flex items-baseline gap-2">
      {effectivePrice ? (
        <>
          <span className="text-white font-bold text-base">
            {formatCLP(effectivePrice)}
          </span>
          {discount_percentage && discount_percentage > 0 && price ? (
            <span className="text-white/70 line-through text-xs">
              {formatCLP(price)}
            </span>
          ) : null}
        </>
      ) : (
        <span className="text-white font-bold text-base">{t('site.vehicles.priceOnRequest')}</span>
      )}
    </div>
  );

  const SpecItem: React.FC<{ icon: React.ReactNode; text: string }> = ({
    icon,
    text,
  }) => (
    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-white/85">
      {icon}
      <span className="leading-none">{text}</span>
    </div>
  );

  const cardInner = (
    <div
      className={`relative overflow-hidden bg-black hover:-translate-y-3 transition duration-500 ${shadow}`}
      style={{ borderRadius: radius }}
    >
      {/* Imagen */}
      <div className={`relative ${compact ? 'h-52' : 'h-64'} `}>
        {main_image ? (
          <Img sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            src={main_image}
            alt={`${brand?.name || ''} ${model?.name || ''}`}
            className="w-full h-full group-hover:opacity-50 duration-500 object-cover transition "
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Car size={48} className="text-gray-300" />
          </div>
        )}

        {/* Chip estado */}
        <div className="absolute top-3 left-3">
          <span
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${statusChip.className}`}
          >
            {statusChip.label}
          </span>
        </div>

        {/* Badge label - only show explicit labels from DB */}
        {label && status?.name !== 'Vendido' && status?.name !== 'Reservado' && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-emerald-100 text-emerald-700 text-[11px] font-medium px-2.5 py-1 rounded-full">
              {label}
            </Badge>
          </div>
        )}

        {/* Overlay gradiente + contenido inferior */}
        <div className="absolute inset-x-0 bottom-0 pointer-events-none">
          <div className="h-28 sm:h-28 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        </div>

        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
          <div className="flex flex-col gap-1.5">
            {Title}
            {Price}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
              {typeof mileage === 'number' && (
                <SpecItem
                  icon={<Gauge size={14} className="opacity-80" />}
                  text={`${mileage.toLocaleString()} ${t('site.vehicles.kmsUnit')}`}
                />
              )}
              {year && (
                <SpecItem
                  icon={<Calendar size={14} className="opacity-80" />}
                  text={String(year)}
                />
              )}
              {transmission && transmission.trim() !== '' && (
                <SpecItem
                  icon={<Settings size={14} className="opacity-80" />}
                  text={transmission}
                />
              )}
              {fuel_type?.name && (
                <SpecItem
                  icon={<Car size={14} className="opacity-80" />}
                  text={fuel_type.name}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isEnabled) {
    return <div className="group">{cardInner}</div>;
  }

  return (
    <a href={`/vehicles/${id}`} className="block group" aria-label={`${brand?.name} ${model?.name}`}>
      {cardInner}
    </a>
  );
};
